import { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";

const storyLinks = [
  {
    title: "Technology events that build real capability",
    description:
      "Explore workshops, bootcamps, and public learning experiences designed to help students, professionals, and communities engage with AI in practical ways.",
    to: "/events",
    cta: "Browse events",
  },
  {
    title: "Follow the ideas shaping the ecosystem",
    description:
      "Visit the blog for a closer look at the lessons, experiments, and technical thinking guiding Jisedai's products, programs, and long-term vision.",
    to: "/blog",
    cta: "Open the blog",
  },
];

export default function VideoShowcase() {
  const shouldReduceMotion = useReducedMotion();
  const videoRef = useRef(null);
  const loopUntilSeconds = 5.6;

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (video.currentTime >= loopUntilSeconds) {
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    }
  };

  return (
    <section className="relative overflow-hidden bg-[#eeeee8] px-5 py-24 text-[#111712] sm:px-8 md:px-12 md:py-32">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/4 top-10 h-64 w-64 rounded-full bg-[#bfd4c4]/45 blur-3xl" />
        <div className="absolute bottom-10 right-1/4 h-72 w-72 rounded-full bg-[#d6e85c]/30 blur-3xl" />
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:items-stretch">
        <motion.div
          initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.65 }}
          className="flex flex-col"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#45695a]">
            In Motion
          </p>
          <h2 className="mt-5 max-w-3xl font-serif text-4xl leading-[0.97] tracking-[-0.05em] text-[#111712] md:text-6xl">
            Jisedai exists to make technology more practical, accessible, and useful
          </h2>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-[#4d5951] md:text-xl">
            Our objective is not only to talk about AI, but to help people and
            organizations use technology to learn better, operate smarter, and
            build with more confidence. Through events, technical programs, and
            applied systems work, Jisedai is building a stronger bridge between
            innovation and everyday impact.
          </p>

          <div className="mt-8 space-y-4">
            {storyLinks.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: shouldReduceMotion ? 0 : -18 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                className="rounded-2xl border border-[#111712]/15 bg-white/45 px-5 py-5"
              >
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#45695a]">
                  {item.title}
                </p>
                <p className="mt-3 leading-7 text-[#4d5951]">
                  {item.description}
                </p>
                <Link
                  to={item.to}
                  className="mt-4 inline-flex text-sm font-bold uppercase tracking-[0.2em] text-[#45695a] transition hover:text-[#111712]"
                >
                  {item.cta}
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: shouldReduceMotion ? 0 : 28 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex h-full"
        >
          <div className="flex w-full overflow-hidden rounded-[2rem] border border-[#111712]/15 bg-[#d9ddd2] p-4 shadow-xl shadow-[#45695a]/10">
            <div className="relative flex min-h-[26rem] w-full items-center justify-center overflow-hidden rounded-[1.6rem] border border-[#111712]/15 bg-[#111712] px-4 py-4 lg:min-h-full">
              <video
                ref={videoRef}
                className="max-h-full w-full rounded-[1.2rem] object-contain"
                src="/jisedai-youth-ai-teaser.mp4"
                autoPlay
                muted
                playsInline
                controls
                preload="metadata"
                onTimeUpdate={handleTimeUpdate}
              >
                Your browser does not support the video tag.
              </video>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent px-6 py-6">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d6e85c]">
                  Jisedai Community
                </p>
                <p className="mt-2 max-w-sm text-sm leading-6 text-white/80">
                  Learning, experimenting, and applying AI to real-world
                  questions across events, programs, and technical collaborations.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
